import React, { Component } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { Query, Mutation } from 'react-apollo'
import {
  PROJECT_QUERY,
  RANK_POSITION_MUTATION,
  DELETE_POSITION_MUTATION,
  COPY_POSITIONS_MUTATION,
} from './'
import { AuthStore } from 'redux/reducers/auth'

import get from 'lodash/get'
import isNil from 'lodash/isNil'
import defaultTo from 'lodash/defaultTo'

import { formatNumber, getPrintTemplateLink, getProjectId } from 'utils'

import Button from 'components/ui/button'
import PageLayout from 'components/layout/pageLayout'
import Head from 'components/layout/head'
import SelectTable, { ISelectItem, IDuplicate } from 'components/layout/selectTable'
import Modal from 'components/ui/modal'
import SubNavigation from 'components/layout/subNavigation'
import PopUpMenu from 'components/ui/popUpMenu'
import TableResult from 'components/layout/tableResult'
import Loader from 'components/ui/loader'

import tableConfig from './projectOverviewTable.config'
import netResultTableConfig from './netResultTable.config'
import grossResultTableConfig from './grossResultTable.config'

interface ProjectsProps extends RouteComponentProps<any> {
  auth: AuthStore
  dispatch: any
}

type ProjectsState = {
  items: ISelectItem[] | null
  isPrintModalOpen: boolean
}

class Projects extends Component<ProjectsProps, ProjectsState> {
  constructor(props: ProjectsProps) {
    super(props)
    this.state = {
      items: null,
      isPrintModalOpen: false,
    }
  }

  // TODO: Handle project save
  handleSave = () => {
    const { items } = this.state

    if (items) {
      // Do save
    }

    // No changes were made
  }

  handlePrint = () => {
    this.setState({ isPrintModalOpen: true })
  }

  handleAddNewPosition = () => {
    const projectId = getProjectId(this.props)
    const { history } = this.props
    history.push(`/projects/${projectId}/new-calculation`)
  }

  handleModalClose = () => {
    this.setState({ isPrintModalOpen: false })
  }

  render() {
    const {
      auth: { apiToken },
    } = this.props
    const { isPrintModalOpen } = this.state
    const projectId = getProjectId(this.props)

    return (
      <Query query={PROJECT_QUERY} variables={{ projectId }} fetchPolicy="network-only">
        {({ loading, data }: any | null) => {
          const projectName = get(data, 'getProject.projectName', undefined)
          const projectSumNetto = get(data, 'getProject.projectSumNetto', undefined)
          const projectSumBrutto = get(data, 'getProject.projectSumBrutto', undefined)
          const tax = get(data, 'getProject.tax', undefined)
          const taxPercentage = get(data, 'getProject.projectDetails.tax', undefined)
          const positionsArr = get(data, 'getProject.positions', undefined)

          let positions: ISelectItem[] = []

          // Transform positions
          if (positionsArr) {
            positions = positionsArr.map((position: any) => {
              if (!isNil(position.amount) && !position.amount.hasOwnProperty('value')) {
                position.amount = {
                  value: position.amount,
                  unit: position.unit,
                }
              }
              return position
            })
          }

          return (
            <Mutation mutation={RANK_POSITION_MUTATION}>
              {(
                rankPosition: (payload: {
                  variables: {
                    version: string
                    projectNumber: string
                    newPosition: number
                    oldPosition: number
                  }
                }) => void,
              ) => (
                <Mutation
                  mutation={COPY_POSITIONS_MUTATION}
                  refetchQueries={() => [{ query: PROJECT_QUERY, variables: { projectId } }]}
                >
                  {(
                    copyPositions: (payload: {
                      variables: {
                        toVersion: string
                        toProjectNumber: string
                        fromPositionIds: number[]
                        fromVersions: string[]
                        from_projectNumber: string
                      }
                    }) => void,
                  ) => (
                    <Mutation mutation={DELETE_POSITION_MUTATION}>
                      {(
                        deletePosition: (payload: {
                          variables: { version: string; positionId: number; projectId: string }
                        }) => void,
                      ) => (
                        <PageLayout pageTitle="Projekte">
                          <Head
                            headline={projectName}
                            subheadline={
                              process.env.REACT_APP_ENV === 'rigips'
                                ? 'Mengenermittlung und Systemsuche - HORST'
                                : 'ABK calcKit – der Baukasten für die schnelle Kalkulation'
                            }
                            actions={
                              <Button primary onClick={this.handleAddNewPosition}>
                                Neue Position hinzufügen
                              </Button>
                            }
                          />
                          <SubNavigation
                            noMargin
                            items={[
                              {
                                label: 'Übersicht',
                                link: `/projects/${projectId}`,
                              },
                              {
                                link: `/projects/${projectId}/material-requirement`,
                                label: 'Materialbedarf',
                              },
                              {
                                link: `/projects/${projectId}/time-requirement`,
                                label: 'Zeitbedarf',
                              },
                              {
                                link: `/projects/${projectId}/settings`,
                                label: 'Einstellungen',
                              },
                            ]}
                            actions={
                              <>
                                <PopUpMenu
                                  className="ml-30"
                                  options={[
                                    {
                                      label: 'Drucken',
                                      function: () => this.handlePrint(),
                                    },
                                    {
                                      label: 'Neue Position hinzufügen',
                                      function: () => this.handleAddNewPosition(),
                                    },
                                  ]}
                                />
                              </>
                            }
                          />
                          <Modal
                            headline="Bitte wählen Sie eine Druckliste"
                            open={isPrintModalOpen}
                            onClose={this.handleModalClose}
                          >
                            <ul className="flex flex-col mt-30 mb-15">
                              <li>
                                <a
                                  className="link"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  href={getPrintTemplateLink('materialRequisition', {
                                    projNr: defaultTo(projectId, ''),
                                    token: defaultTo(apiToken, ''),
                                  })}
                                >
                                  Materialbedarfsliste
                                </a>
                              </li>
                              <li className="mt-10">
                                <a
                                  className="link"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  href={getPrintTemplateLink('materialOrderList', {
                                    projNr: defaultTo(projectId, ''),
                                    token: defaultTo(apiToken, ''),
                                  })}
                                >
                                  Materialbestellliste
                                </a>
                              </li>
                              <li className="mt-10">
                                <a
                                  className="link"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  href={getPrintTemplateLink('timeRequisition', {
                                    projNr: defaultTo(projectId, ''),
                                    token: defaultTo(apiToken, ''),
                                  })}
                                >
                                  Zeitbedarfsliste
                                </a>
                              </li>
                            </ul>
                          </Modal>

                          {loading ? (
                            <Loader />
                          ) : (
                            <>
                              <SelectTable
                                config={tableConfig(defaultTo(projectId, ''))}
                                onOrderChange={(
                                  source: number,
                                  destination: number,
                                  item: ISelectItem,
                                ) => {
                                  rankPosition({
                                    variables: {
                                      version: String(item.id),
                                      projectNumber: defaultTo(projectId, ''),
                                      newPosition: destination,
                                      oldPosition: source,
                                    },
                                  })
                                }}
                                onDelete={(items: ISelectItem[]) => {
                                  items.map((item) => {
                                    deletePosition({
                                      variables: {
                                        version: 'v1',
                                        positionId: Number(item.id),
                                        projectId: defaultTo(projectId, ''),
                                      },
                                    })
                                    return item
                                  })
                                }}
                                onDuplicate={(duplicates) => {
                                  const positionIds: number[] = duplicates.map(
                                    (duplicate: IDuplicate) => Number(duplicate.id),
                                  )

                                  copyPositions({
                                    variables: {
                                      fromVersions: ['v1'],
                                      toVersion: 'v1',
                                      from_projectNumber: defaultTo(projectId, ''),
                                      toProjectNumber: defaultTo(projectId, ''),
                                      fromPositionIds: positionIds,
                                    },
                                  })
                                }}
                                data={positions}
                              />
                              <div className="my-65">
                                <TableResult
                                  config={netResultTableConfig}
                                  data={[
                                    {
                                      nettoPrice: {
                                        value: projectSumNetto,
                                        unit: 'EUR',
                                      },
                                    },
                                  ]}
                                />
                                <TableResult
                                  config={grossResultTableConfig(formatNumber(taxPercentage, '%'))}
                                  data={[
                                    {
                                      totalPriceNetto: projectSumNetto,
                                      tax: projectSumNetto * (tax / 100),
                                      totalPriceBrutto: projectSumBrutto,
                                    },
                                  ]}
                                />
                              </div>
                            </>
                          )}
                        </PageLayout>
                      )}
                    </Mutation>
                  )}
                </Mutation>
              )}
            </Mutation>
          )
        }}
      </Query>
    )
  }
}

export default Projects
